import { Alert, Input, Space } from "antd";
import type { OrderFinishForm, OrderVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Button as ZhButton, Dialog as ZhDialog } from "zhihao-ui-react";

export interface TradeOrderFinishDialogProps {
    order: OrderVo | null;
    mode?: "finish" | "edit";
    initialForm?: OrderFinishForm;
    submitting?: boolean;
    errorMessage?: string | null;
    onCancel: () => void;
    onConfirm: (form: OrderFinishForm) => void;
}

/** 交易订单完成弹窗，沿用 Vue 的完成提示、吨数和结算凭证字段。 */
export function TradeOrderFinishDialog({
    order,
    mode = "finish",
    initialForm,
    submitting = false,
    errorMessage,
    onCancel,
    onConfirm
}: TradeOrderFinishDialogProps) {
    const [pickedUpTon, setPickedUpTon] = useState("");
    const [settlementIds, setSettlementIds] = useState("");
    const [formError, setFormError] = useState<string | null>(null);
    const initialSettlementIds = (initialForm?.settlementIds || []).join(", ");

    useEffect(() => {
        if (order === null) {
            setPickedUpTon("");
            setSettlementIds("");
            setFormError(null);
        } else if (initialForm) {
            setPickedUpTon(initialForm.pickedUpTon == null ? "" : String(initialForm.pickedUpTon));
            setSettlementIds(initialSettlementIds);
        }
    }, [initialForm?.pickedUpTon, initialSettlementIds, order]);

    const submit = () => {
        const normalizedTon = pickedUpTon.trim();
        if (normalizedTon && (!Number.isFinite(Number(normalizedTon)) || Number(normalizedTon) <= 0)) {
            setFormError("请输入正确的已提货吨数");
            return;
        }
        setFormError(null);
        const parsedSettlementIds = settlementIds
            .split(",")
            .map(item => Number(item.trim()))
            .filter(value => Number.isInteger(value) && value > 0);
        onConfirm({
            ...(normalizedTon ? { pickedUpTon: Number(normalizedTon) } : {}),
            ...(parsedSettlementIds.length ? { settlementIds: parsedSettlementIds } : {})
        });
    };

    return (
        <ZhDialog
            open={order !== null}
            title={mode === "edit" ? "修改" : "确认完成"}
            width="600px"
            onCancel={onCancel}
            footer={
                <Space>
                    <ZhButton disabled={submitting} onClick={onCancel}>
                        取消
                    </ZhButton>
                    <ZhButton loading={submitting} type="primary" onClick={submit}>
                        确认
                    </ZhButton>
                </Space>
            }
        >
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            {formError ? <Alert className="mb-4" message={formError} role="alert" type="error" /> : null}
            {mode === "finish" ? <div className="mb-4">是否完成，完成后不能再提货</div> : null}
            <label className="mb-2 block" htmlFor="trade-order-finish-picked-up-ton">
                已提货吨数
            </label>
            <Input
                id="trade-order-finish-picked-up-ton"
                aria-label="已提货吨数"
                className="mb-4"
                min={0.01}
                placeholder="请输入已提货吨数"
                type="number"
                value={pickedUpTon}
                onChange={event => setPickedUpTon(event.target.value)}
            />
            <label className="mb-2 block" htmlFor="trade-order-finish-settlement-ids">
                结算凭证
            </label>
            <Input
                id="trade-order-finish-settlement-ids"
                aria-label="结算凭证"
                placeholder="请输入结算凭证 ID，多个用逗号分隔"
                value={settlementIds}
                onChange={event => setSettlementIds(event.target.value)}
            />
        </ZhDialog>
    );
}

export default TradeOrderFinishDialog;
